/**
 * UI-specific types and schemas
 *
 * This module contains types used primarily in the UI layer,
 * including draft states, temporary data structures, and UI-specific schemas.
 */

import { z } from 'zod';

/**
 * Schema for draft attachments (files being prepared for upload/send)
 * Used in chat interface for managing files before they're sent
 */
export const DraftAttachmentSchema = z.object({
  id: z.string().describe('Unique identifier for this attachment'),
  name: z.string().describe('Original filename'),
  contentType: z.string().describe('MIME type of the file'),
  url: z.string().describe('Data URL for local preview'),
  isScreenshot: z.boolean().describe('Whether this is a screenshot or uploaded file'),
  base64: z.string().optional().describe('Base64 encoded image data'),
  processing: z.boolean().optional().describe('Base64 conversion in progress'),
  processingError: z.string().optional().describe('Processing error message if any'),
  s3Key: z.string().optional().describe('S3 key after successful upload'),
  presignedUrl: z.string().optional().describe('Presigned S3 URL for backend access'),
  estimatedTokens: z.number().optional().describe('Estimated token count for this attachment'),
  fileSizeMB: z.number().optional().describe('File size in megabytes'),
});

export type DraftAttachment = z.infer<typeof DraftAttachmentSchema> & {
  // Browser File object - not serializable, only exists in browser context
  file?: File;
};

/**
 * Schema for draft attachment metadata (for API calls)
 */
export const DraftAttachmentMetadataSchema = z.object({
  isScreenshot: z.boolean(),
  filename: z.string(),
  description: z.string(),
});

export type DraftAttachmentMetadata = z.infer<typeof DraftAttachmentMetadataSchema>;

/**
 * Schema for file upload progress state
 */
export const FileUploadProgressSchema = z.object({
  fileId: z.string().describe('Local file identifier'),
  filename: z.string(),
  progress: z.number().min(0).max(100).describe('Processing progress percentage'),
  status: z.enum(['pending', 'processing', 'completed', 'error']),
  error: z.string().optional(),
  base64: z.string().optional().describe('Base64 encoded data when completed'),
});

export type FileUploadProgress = z.infer<typeof FileUploadProgressSchema>;

/**
 * Schema for draft message state in chat interface
 */
export const DraftMessageSchema = z.object({
  content: z.string(),
  attachments: z.array(DraftAttachmentSchema),
  screenshots: z.array(DraftAttachmentSchema),
  timestamp: z.number().describe('Draft creation timestamp'),
});

export type DraftMessage = z.infer<typeof DraftMessageSchema>;
